import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getConfigManager } from '../config/getConfigManager';
import { Transaction, PointsUser, Nullable, RewardPeriod } from '../interfaces';
import { ANALYTICS_START_TIMESTAMP, ASSETS } from '../constants';
import { Decimal } from '../datastructures';

const API_ENDPOINT = import.meta.env.VITE_APP_API_ENDPOINT!;

const MAX_RETRIES = 3;

const apiClient = axios.create();

apiClient.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as InternalAxiosRequestConfig & { __retryCount?: number } | undefined;
  if (!config || error.response?.status !== 429) {
    return Promise.reject(error);
  }

  config.__retryCount = config.__retryCount ?? 0;
  if (config.__retryCount >= MAX_RETRIES) {
    return Promise.reject(error);
  }

  config.__retryCount++;

  const retryAfterHeader = error.response?.headers?.['retry-after'];
  const delay = retryAfterHeader
    ? parseInt(retryAfterHeader, 10) * 1000
    : Math.min(2 ** config.__retryCount * 1000, 8000); // 2s, 4s, 8s

  await new Promise(resolve => setTimeout(resolve, delay));
  return apiClient(config);
});

enum TransactionType {
  Deposit = 'DEPOSIT',
  Withdraw = 'WITHDRAW',
  Borrow = 'BORROW',
  Repay = 'REPAY',
  Adjust = 'ADJUST',
}

type TransactionToken = {
  tokenAddress: string;
};

type AdjustTransactionTokens = {
  fromTokenAddress: string;
  toTokenAddress: string;
};

type CommonTransactionData = {
  id: string;
  hash: string;
  timestamp: number;
  amount: string;
  account: string;
};

export type StorageTransaction = CommonTransactionData &
  (
    | {
      type: TransactionType.Deposit | TransactionType.Withdraw | TransactionType.Borrow | TransactionType.Repay;
      tokens: TransactionToken;
    }
    | {
      type: TransactionType.Adjust;
      tokens: AdjustTransactionTokens;
    }
  );

const TRANSACTIONS_TABLE = 'transactions';
const SUPPLIERS_AND_BORROWERS_NUMBERS_TABLE = 'suppliersAndBorrowersNumbers';
const TOTAL_SUPPLY_AND_BORROWS_TABLE = 'totalSupplyAndBorrows';
const SUPPLY_AND_BORROW_TABLE = 'supplyAndBorrow';
const APY_STATS_TABLE = 'apyStats';
const USER_POINTS_TABLE = 'userPoints';
const USER_POINTS_METADATA = 'pointsMetadata';
const USERS_TABLE = 'users';
const BALANCES_TABLE = 'balances';
const SWAPS_TABLE = 'swaps';
const POOLS_TABLE = 'pools';
const POOLS_SWAPS_TABLE = 'poolSwaps';
const REWARDS_TABLE = 'rewards';
const REWARD_PROOFS_TABLE = 'rewardProofs';
const REWARD_PERIODS_TABLE = 'rewardPeriods';
const AIRDROP_PROOFS_TABLE = 'airdropProofs';
const TVL_TABLE = 'tvl';
const FEES_TABLE = 'fees';
const VOLUME_TABLE = 'volume';

class Storage {
  private database: string;

  constructor(database: string) {
    this.database = database;
  }

  async getNumberOfLendersAndBorrowers(): Promise<{ lenders: number; borrowers: number }> {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: SUPPLIERS_AND_BORROWERS_NUMBERS_TABLE,
      filter: {},
      limit: 1,
      sort: {
        timestamp: -1,
      },
    });

    const lastDoc = result.data.documents[0];
    if (!lastDoc) {
      return {
        lenders: 0,
        borrowers: 0,
      };
    }

    let lenders = 0;
    let borrowers = 0;
    ASSETS.forEach(asset => {
      const assetData = lastDoc[asset];
      if (!assetData) {
        return;
      }

      lenders += Number(assetData.lendersCount);
      borrowers += Number(assetData.borrowersCount);
    });

    return {
      lenders,
      borrowers,
    };
  }

  async getTransactionHistory(account: string, fromDate?: Date, toDate?: Date): Promise<Transaction[]> {
    let filter = {};
    if (fromDate) {
      filter = {
        ...filter,
        timestamp: {
          $gte: fromDate.getTime() / 1000,
        },
      };
    }

    if (toDate) {
      filter = {
        ...filter,
        timestamp: {
          $lte: toDate.getTime() / 1000,
        },
      };
    }
    filter = {
      ...filter,
      account: account,
    };

    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: TRANSACTIONS_TABLE,
      filter: filter,
      sort: {
        timestamp: -1,
      },
    });

    const storageTransactions: StorageTransaction[] = result.data.documents.map(document => {
      return {
        account: document.account,
        amount: document.amount,
        hash: document.hash,
        id: document.id,
        timestamp: document.timestamp,
        type: document.type,
        tokens: document.tokens,
      };
    });

    const configManager = getConfigManager();
    const transactions: (Transaction | null)[] = storageTransactions.map(storageTransaction => {
      let assetAddress = '';

      try {
        if (storageTransaction.type === TransactionType.Adjust && storageTransaction.tokens.toTokenAddress) {
          assetAddress = configManager.getNostraTokenAssetAddress(storageTransaction.tokens.toTokenAddress);
        } else if (storageTransaction.type !== TransactionType.Adjust) {
          assetAddress = configManager.getNostraTokenAssetAddress(storageTransaction.tokens.tokenAddress);
        }
      } catch (e) {
        // If user has a BROTHER transaction or any other asset that is removed from config, above will fail - just ignore
        console.error(e);
      }

      if (!assetAddress) {
        return null;
      }

      const asset = configManager.getAssetByAddress(assetAddress);

      if (!asset) {
        throw new Error(`Failed to find asset for nostra token address ${assetAddress}`);
      }

      if (storageTransaction.type === TransactionType.Adjust) {
        return {
          hash: storageTransaction.hash,
          date: new Date(storageTransaction.timestamp * 1000),
          type: storageTransaction.type,
          amount: new Decimal(BigInt(storageTransaction.amount), asset.tokenPrecision),
          tokens: {
            fromTokenAddress: storageTransaction.tokens.fromTokenAddress,
            toTokenAddress: storageTransaction.tokens.toTokenAddress,
          },
        };
      } else {
        return {
          hash: storageTransaction.hash,
          date: new Date(storageTransaction.timestamp * 1000),
          type: storageTransaction.type,
          amount: new Decimal(BigInt(storageTransaction.amount), asset.tokenPrecision),
          tokens: {
            tokenAddress: storageTransaction.tokens.tokenAddress,
          },
        };
      }
    });

    return transactions.filter(transaction => transaction !== null) as Transaction[];
  }

  async getTotalSupplyAndBorrowsData() {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: TOTAL_SUPPLY_AND_BORROWS_TABLE,
      filter: {
        timestamp: {
          $gte: ANALYTICS_START_TIMESTAMP,
        },
      },
      sort: {
        timestamp: -1,
      },
    });

    return result.data.documents;
  }

  async getSupplyAndBorrowHistoryData(fromTimestamp: Nullable<number>) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: SUPPLY_AND_BORROW_TABLE,
      filter: {
        timestamp: {
          $gte: fromTimestamp ?? 0,
        },
      },
      sort: {
        timestamp: 1,
      },
    });

    return result.data.documents;
  }

  async getPoolTvlHistoryData(fromTimestamp: Nullable<number>) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: TVL_TABLE,
      filter: {
        timestamp: {
          $gte: fromTimestamp ?? 0,
        },
      },
      sort: {
        timestamp: 1,
      },
    });

    return result.data.documents;
  }

  async getPoolVolumeHistoryData(fromTimestamp: Nullable<number>) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: VOLUME_TABLE,
      filter: {
        startTime: {
          $gte: fromTimestamp ?? 0,
        },
      },
      sort: {
        startTime: 1,
      },
    });

    const data = result.data.documents;

    const startTime = data[0].startTime;
    const dataInterval = 86400; // 1 day
    const currentTime = Date.now() / 1000 - dataInterval;

    const poolsConfig = getConfigManager().getConfig().pools;

    const poolsWithSwaps: string[] = [];

    for (let i = startTime; i < currentTime; i += dataInterval) {
      const found = data.find(item => item.startTime > i - dataInterval / 2 && item.startTime < i + dataInterval / 2);
      if (found) {
        Object.entries(found.pools).forEach(item => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const pool: any = item[1];

          poolsWithSwaps.push(pool.id);
        });
      }

      if (!found) {
        const startTime = i;
        const endTime = i + dataInterval;

        const pools = {};
        Object.entries(poolsConfig).forEach(([, poolConfig]) => {
          if (poolsWithSwaps.includes(poolConfig.pair.id)) {
            pools[poolConfig.pair.id] = {
              id: poolConfig.pair.id,
              volume: '0',
            };
          }
        });

        data.push({
          endTime: endTime,
          id: `${startTime}-${endTime}`,
          pools: pools,
          startTime: startTime,
        });
      } else {
        const pools = found.pools;
        Object.entries(poolsConfig).forEach(([, poolConfig]) => {
          if (!pools[poolConfig.pair.id] && poolsWithSwaps.includes(poolConfig.pair.id)) {
            pools[poolConfig.pair.id] = {
              id: poolConfig.pair.id,
              volume: '0',
            };
          }
        });
      }
    }

    return result.data.documents.sort((a, b) => a.startTime - b.startTime);
  }

  async getPoolFeesHistoryData(fromTimestamp: Nullable<number>) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: FEES_TABLE,
      filter: {
        startTime: {
          $gte: fromTimestamp ?? 0,
        },
      },
      sort: {
        startTime: 1,
      },
    });

    const data = result.data.documents;

    const startTime = data[0].startTime;
    const currentTime = Date.now() / 1000;
    const dataInterval = 86400; // 1 day

    const poolsConfig = getConfigManager().getConfig().pools;

    const poolsWithSwaps: string[] = [];

    for (let i = startTime; i < currentTime; i += dataInterval) {
      const found = data.find(item => item.startTime > i - dataInterval / 2 && item.startTime < i + dataInterval / 2);
      if (found) {
        Object.entries(found.pools).forEach(item => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const pool: any = item[1];

          poolsWithSwaps.push(pool.id);
        });
      }

      if (!found) {
        const startTime = i;
        const endTime = i + dataInterval;

        const pools = {};
        Object.entries(poolsConfig).forEach(([, poolConfig]) => {
          if (poolsWithSwaps.includes(poolConfig.pair.id)) {
            pools[poolConfig.pair.id] = {
              id: poolConfig.pair.id,
              fees: '0',
            };
          }
        });

        data.push({
          endTime: endTime,
          id: `${startTime}-${endTime}`,
          pools: pools,
          startTime: startTime,
        });
      } else {
        const pools = found.pools;
        Object.entries(poolsConfig).forEach(([, poolConfig]) => {
          if (!pools[poolConfig.pair.id] && poolsWithSwaps.includes(poolConfig.pair.id)) {
            pools[poolConfig.pair.id] = {
              id: poolConfig.pair.id,
              fees: '0',
            };
          }
        });
      }
    }

    return result.data.documents.sort((a, b) => a.startTime - b.startTime);
  }

  async getApyData() {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: APY_STATS_TABLE,
      filter: {
        timestamp: {
          $gte: ANALYTICS_START_TIMESTAMP,
        },
      },
      sort: {
        timestamp: -1,
      },
    });

    return result.data.documents;
  }

  /**
   * @returns Returns top 10 users with the most points
   */
  async getPointUsers(season: number, skip: number, limit: number) {
    const usersResponse = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: USER_POINTS_TABLE,
      filter: {
        season,
      },
      sort: {
        rank: 1,
      },
      limit,
      skip: skip,
    });

    return usersResponse.data.documents;
  }

  async getPointUsersAroundRank(pointUser: PointsUser, season: number, limit: number) {
    const usersResponse = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: USER_POINTS_TABLE,
      filter: { rank: pointUser.rank, season },
      sort: {
        id: 1,
      },
      limit,
    });

    const usersWithSameRank = usersResponse.data.documents;
    const usersAround = usersWithSameRank.filter(point => pointUser.account !== point.account);
    const currentUserPoint = usersWithSameRank.find(point => pointUser.account === point.account);
    const beforeLength = Math.floor((limit - 1) / 2);

    if (usersWithSameRank.length >= limit) {
      return [
        ...usersAround.slice(0, beforeLength),
        currentUserPoint,
        ...usersAround.slice(beforeLength + 1, limit - 1),
      ];
    }

    const beforeUsersResponse = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: USER_POINTS_TABLE,
      filter: { rank: { $lt: pointUser.rank }, season },
      sort: {
        rank: -1,
      },
      limit: beforeLength,
    });
    const afterUsersResponse = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: USER_POINTS_TABLE,
      filter: { rank: { $gt: pointUser.rank }, season },
      sort: {
        rank: 1,
      },
      limit: limit - beforeLength - usersWithSameRank.length,
    });

    const beforeUsers = beforeUsersResponse.data.documents.reverse();
    const middleUsers = [currentUserPoint, ...usersAround];
    const afterUsers = afterUsersResponse.data.documents;

    return [...beforeUsers, ...middleUsers, ...afterUsers];
  }

  async getUserPoints(account: string, season: number) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/findOne`, {
      database: this.database,
      collection: USER_POINTS_TABLE,
      filter: {
        account: account,
        season,
      },
    });

    return result.data.document;
  }

  async getPointsMetadata() {
    const result = await apiClient.post(`${API_ENDPOINT}/query/findOne`, {
      database: this.database,
      collection: USER_POINTS_METADATA,
      filter: {
        id: 'metadata',
      },
    });

    return result.data.document;
  }

  async getUserReferralCode(account: string): Promise<string | null> {
    const result = await apiClient.post(`${API_ENDPOINT}/query/findOne`, {
      database: this.database,
      collection: USERS_TABLE,
      filter: {
        account: account,
      },
    });

    if (!result.data.document) {
      return null;
    }

    return result.data.document.referralCode;
  }

  async getUserSubaccounts(account: string): Promise<string[] | null> {
    const result = await apiClient.post(`${API_ENDPOINT}/query/findOne`, {
      database: this.database,
      collection: USERS_TABLE,
      filter: {
        account: account,
      },
    });

    if (!result.data.document) {
      return null;
    }

    return result.data.document.subaccounts;
  }

  async getUserReferee(account: string): Promise<string | null> {
    const result = await apiClient.post(`${API_ENDPOINT}/query/findOne`, {
      database: this.database,
      collection: USERS_TABLE,
      filter: {
        account: account,
      },
    });

    if (!result.data.document) {
      return null;
    }

    return result.data.document.referrer;
  }

  async getAccountsBalances(account: string) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: BALANCES_TABLE,
      filter: {
        accountAddress: account,
      },
    });

    return result.data.documents;
  }

  async getRefereesValue(referralCode: string) {
    const referredUsersResult = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: USERS_TABLE,
      filter: { referrer: referralCode },
    });

    const referredUsers = referredUsersResult.data.documents;

    const value = await Promise.all(
      referredUsers.map(async referredUser => {
        const balancesResult = await apiClient.post(`${API_ENDPOINT}/query/find`, {
          database: this.database,
          collection: BALANCES_TABLE,
          filter: { accountAddress: referredUser.account },
        });

        const referredUserBalances = balancesResult.data.documents;

        const balances = referredUserBalances.map(balance => {
          return {
            tokenAddress: balance.tokenAddress,
            asset: balance.asset,
            amount: balance.amount,
          };
        });

        return balances;
      }),
    );

    const allValues = value.flat();

    const totalValuePerAsset: { [asset: string]: Decimal } = {};
    allValues.forEach(value => {
      if (!totalValuePerAsset[value.asset]) {
        totalValuePerAsset[value.asset] = Decimal.ZERO;
      }

      const assetPrecision = getConfigManager().getTokenPrecision(value.asset);

      totalValuePerAsset[value.asset] = totalValuePerAsset[value.asset].add(
        new Decimal(BigInt(value.amount), assetPrecision),
      );
    });

    return totalValuePerAsset;
  }

  async getDirectRefereesCount(referralCode: string): Promise<number> {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: USERS_TABLE,
      filter: { referrer: referralCode },
    });

    return result.data.documents.length;
  }

  async getAvnuSwapTransactionHistory(account: string) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: SWAPS_TABLE,
      filter: { account: account },
      sort: {
        timestamp: -1,
      },
    });

    return result.data.documents;
  }

  async getNostraSwapTransactionHistory(account: string) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: POOLS_SWAPS_TABLE,
      filter: { walletAddress: account },
      sort: {
        timestamp: -1,
      },
    });

    return result.data.documents;
  }

  async getPoolTransactionHistory(account: string) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: POOLS_TABLE,
      filter: { account: account },
      sort: {
        timestamp: -1,
      },
    });

    return result.data.documents;
  }

  async getPoolSupplyData(poolPairAddress: string, timestamp: number) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: POOLS_TABLE,
      filter: { timestamp: { $lte: timestamp }, pairAddress: poolPairAddress },
      sort: {
        timestamp: -1,
      },
      limit: 1,
    });

    return result.data.documents[0];
  }

  async getPoolLastPrice(poolPairAddress: string, tokenAPrecision: number, tokenBPrecision: number) {
    const lastSwapResponse = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: POOLS_SWAPS_TABLE,
      filter: {
        pairAddress: poolPairAddress,
      },
      sort: {
        timestamp: -1,
      },
      limit: 1,
    });

    const lastSwap = lastSwapResponse.data.documents[0];

    if (!lastSwap) {
      return Decimal.ZERO;
    }

    if (lastSwap.tokenAAmountIn !== '0' && lastSwap.tokenBAmountOut !== '0') {
      const tokenAAmount = new Decimal(BigInt(lastSwap.tokenAAmountIn), tokenAPrecision);
      const tokenBAmount = new Decimal(BigInt(lastSwap.tokenBAmountOut), tokenBPrecision);

      return tokenBAmount.div(tokenAAmount);
    }

    if (lastSwap.tokenBAmountIn !== '0' && lastSwap.tokenAAmountOut !== '0') {
      const tokenAAmount = new Decimal(BigInt(lastSwap.tokenAAmountOut), tokenAPrecision);
      const tokenBAmount = new Decimal(BigInt(lastSwap.tokenBAmountIn), tokenBPrecision);

      return tokenBAmount.div(tokenAAmount);
    }

    return Decimal.ZERO;
  }

  async getUserRewards(walletAddress: string) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: REWARDS_TABLE,
      filter: {
        account: walletAddress,
      },
    });

    return result.data.documents;
  }

  async getUserClaimProofs(walletAddress: string) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: REWARD_PROOFS_TABLE,
      filter: {
        account: walletAddress,
      },
    });

    return result.data.documents;
  }

  async getUserAirdropClaimProof(walletAddress: string) {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: AIRDROP_PROOFS_TABLE,
      filter: {
        account: walletAddress,
      },
    });

    return result.data.documents;
  }

  async getRewardPeriods(): Promise<RewardPeriod[]> {
    const result = await apiClient.post(`${API_ENDPOINT}/query/find`, {
      database: this.database,
      collection: REWARD_PERIODS_TABLE,
    });

    return result.data.documents as RewardPeriod[];
  }
}

export const storage = new Storage(`${import.meta.env.VITE_APP_DATABASE_PREFIX}-nostra-db`);
export const lendAndBorrowAnalyticsStorage = new Storage(
  `${import.meta.env.VITE_APP_LEND_AND_BORROW_ANALYTICS_DATABASE_PREFIX}-nostra-db`,
);
export const poolAnalyticsStorage = new Storage(`${import.meta.env.VITE_APP_POOL_ANALYTICS_DATABASE_PREFIX}-nostra-db`);
